import React, { useState, useEffect } from 'react';
import { X, FileText, Users, ClipboardList, MoreHorizontal, MapPin, Building2, Phone } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { projectService } from '../../services/project.service';
import { organizationService } from '../../services/organization.service';
import { Project, ProjectType, ProjectUser, SchoolAddress, SchoolSegment } from '../../types/project.types';
import { SchoolSegmentsEditor } from './SchoolSegmentsEditor';
import { Organization } from '../../types/organization.types';
import { toast } from 'react-hot-toast';
import { Timestamp, doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { contractGenerationService } from '../../services/contractGeneration.service';

interface CreateProjectModalProps {
  isOpen: boolean;
  onClose: () => void;
  onProjectCreated: (projectId: string) => void;
}

const PROJECT_TYPES: { type: ProjectType; label: string; description: string; icon: React.ElementType }[] = [
  {
    type: 'lead_program',
    label: 'Lead Program',
    description: 'Gather leads with conversion rate tracking. Track leads captured, validated, and enrolled.',
    icon: FileText
  },
  {
    type: 'enrollment',
    label: 'Enrollment',
    description: 'Same as leads but billing focuses on enrollments. Track the same items with enrollment as the goal.',
    icon: Users
  },
  {
    type: 'survey',
    label: 'Survey',
    description: 'Survey work using workloads and the survey system. No lead tracking.',
    icon: ClipboardList
  },
  {
    type: 'other',
    label: 'Other',
    description: 'Custom project type. Configure settings manually.',
    icon: MoreHorizontal
  },
];

export const CreateProjectModal: React.FC<CreateProjectModalProps> = ({
  isOpen,
  onClose,
  onProjectCreated
}) => {
  const { currentUser, userProfile } = useAuth();
  const [step, setStep] = useState(0); // Start at 0 for type selection
  const [creating, setCreating] = useState(false);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [selectedOrgId, setSelectedOrgId] = useState<string>('');
  const [isPrimaryAdmin, setIsPrimaryAdmin] = useState(false);
  const [schoolSegments, setSchoolSegments] = useState<SchoolSegment[]>([]);

  // Check if user is primary admin of their organization
  useEffect(() => {
    const checkPrimaryAdmin = async () => {
      if (!userProfile?.organizationId) {
        setIsPrimaryAdmin(false);
        return;
      }
      try {
        const org = await organizationService.get(userProfile.organizationId);
        setIsPrimaryAdmin(org?.primaryAdminId === userProfile.uid);
      } catch (error) {
        console.error('Error checking primary admin status:', error);
        setIsPrimaryAdmin(false);
      }
    };
    checkPrimaryAdmin();
  }, [userProfile?.organizationId, userProfile?.uid]);

  // Fiscal privacy: provider_admin OR primary admin of an org can see bill rate
  // Only provider roles can see/edit internal pay rates (margin protection)
  const canEditBilling = userProfile?.role === 'provider_admin' || isPrimaryAdmin;
  const isProvider = userProfile?.role === 'provider_admin' || userProfile?.role === 'provider_user';
  const [formData, setFormData] = useState({
    projectType: '' as ProjectType | '',
    name: '',
    description: '',
    startDate: new Date().toISOString().split('T')[0],
    endDate: '',
    leadGoal: 100,
    validLeadGoal: 80,
    enrollmentGoal: 20,
    surveyGoal: 100,
    totalDoors: 1000,
    anticipatedSurveyRate: 10, // percentage
    ratePerLead: 5,
    ratePerValidLead: 10,
    ratePerEnrollment: 25,
    ratePerSurvey: 5,
    payRate: 3,
    paymentTerms: 'weekly' as const,
    // School address fields
    schoolAddress: '',
    schoolCity: '',
    schoolState: '',
    schoolZipCode: '',
    schoolPhone: ''
  });

  // Load organizations for provider_admin
  useEffect(() => {
    if (userProfile?.role === 'provider_admin') {
      organizationService.getActive().then(setOrganizations).catch(console.error);
    }
  }, [userProfile]);

  const handleSubmit = async () => {
    if (!currentUser || !userProfile || !formData.projectType) return;

    setCreating(true);
    try {
      // Determine billing rate based on project type
      const billingRate = formData.projectType === 'enrollment'
        ? formData.ratePerEnrollment
        : formData.projectType === 'survey'
        ? formData.ratePerSurvey
        : formData.ratePerValidLead;

      // Build school address if provided (skip geocoding - not critical for project creation)
      let schoolAddress: SchoolAddress | undefined;
      if (formData.schoolAddress) {
        schoolAddress = {
          address: formData.schoolAddress,
          city: formData.schoolCity,
          state: formData.schoolState,
          zipCode: formData.schoolZipCode,
          schoolPhone: formData.schoolPhone || ''
        };
      }

      // Determine organization - provider_admin can select, others use their own
      const orgId = userProfile.role === 'provider_admin' && selectedOrgId
        ? selectedOrgId
        : userProfile.organizationId || '';
      const selectedOrg = organizations.find(o => o.id === orgId);

      const projectData: Omit<Project, 'id' | 'createdAt' | 'updatedAt'> = {
        name: formData.name,
        description: formData.description,
        organizationId: orgId,
        organizationName: selectedOrg?.name || userProfile.organizationName || '',
        projectType: formData.projectType,
        status: 'active',
        settings: {
          startDate: Timestamp.fromDate(new Date(formData.startDate + 'T12:00:00')),
          ...(formData.endDate && { endDate: Timestamp.fromDate(new Date(formData.endDate + 'T12:00:00')) }),
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          leadCaptureEnabled: formData.projectType !== 'survey',
          requireValidation: formData.projectType === 'lead_program' || formData.projectType === 'enrollment',
          validationDeadlineDays: 2,
          billingInfo: {
            ratePerValidLead: billingRate,
            payRate: formData.payRate,
            currency: 'USD',
            paymentTerms: formData.paymentTerms,
            invoiceEmails: [userProfile.email]
          }
        },
        goals: {
          validLeads: formData.projectType === 'survey' ? 0 : formData.validLeadGoal,
          enrollmentTarget: formData.projectType === 'survey' ? 0 : formData.enrollmentGoal,
          conversionRateTarget: formData.projectType === 'survey' ? 0 : 25
        },
        assignedUsers: await (async () => {
          const users: ProjectUser[] = [{
            userId: currentUser.uid,
            role: userProfile.role,
            permissions: ['view_leads', 'edit_leads', 'view_reports', 'manage_users', 'edit_settings'],
            assignedAt: Timestamp.now()
          }];

          // Auto-add org's primary admin if this is a client org project
          if (selectedOrg?.primaryAdminId && selectedOrg.primaryAdminId !== currentUser.uid) {
            try {
              const adminDoc = await getDoc(doc(db, 'users', selectedOrg.primaryAdminId));
              if (adminDoc.exists()) {
                users.push({
                  userId: selectedOrg.primaryAdminId,
                  role: adminDoc.data().role || 'client_admin',
                  permissions: ['view_leads', 'view_reports'],
                  assignedAt: Timestamp.now()
                });
              }
            } catch (err) {
              console.error('Failed to fetch org primary admin:', err);
            }
          }
          return users;
        })(),
        createdBy: currentUser.uid,
        ...(schoolAddress && { schoolAddress }),
        ...(formData.projectType !== 'survey' && schoolSegments.filter(s => s.name.trim()).length > 0
          ? { schoolSegments: schoolSegments.filter(s => s.name.trim()) }
          : {})
      };

      const projectId = await projectService.createProject(projectData);

      // Sync org primary admin's user.projects[] array
      if (selectedOrg?.primaryAdminId && selectedOrg.primaryAdminId !== currentUser.uid) {
        try {
          const adminRef = doc(db, 'users', selectedOrg.primaryAdminId);
          const adminDoc = await getDoc(adminRef);
          if (adminDoc.exists()) {
            const currentProjects: string[] = adminDoc.data().projects || [];
            if (!currentProjects.includes(projectId)) {
              await updateDoc(adminRef, { projects: [...currentProjects, projectId] });
            }
          }
        } catch (err) {
          console.error('Failed to sync org admin projects:', err);
        }
      }

      // Auto-generate service agreement draft for client organization projects
      if (orgId && orgId !== userProfile.organizationId) {
        try {
          await contractGenerationService.generateServiceAgreementPDF(projectId);
          toast.success('Project created with service agreement draft!');
        } catch (err) {
          console.error('Failed to generate service agreement:', err);
          toast.success('Project created successfully! (Service agreement generation failed - you can generate it manually from Contracts page)');
        }
      } else {
        toast.success('Project created successfully!');
      }

      onProjectCreated(projectId);
      onClose();
    } catch (error) {
      console.error('Error creating project:', error);
      toast.error('Failed to create project');
    } finally {
      setCreating(false);
    }
  };

  const getTotalSteps = () => {
    return 4; // Type, Details, Goals, Billing (all types have 4 steps)
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b flex justify-between items-center">
          <h2 className="text-2xl font-bold">Create New Project</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6">
          {/* Step 0: Project Type Selection */}
          {step === 0 && (
            <div className="space-y-4">
              <h3 className="text-lg font-medium mb-4">Select Project Type</h3>
              <div className="grid grid-cols-2 gap-4">
                {PROJECT_TYPES.map((pt) => {
                  const Icon = pt.icon;
                  const isSelected = formData.projectType === pt.type;
                  return (
                    <button
                      key={pt.type}
                      onClick={() => setFormData({ ...formData, projectType: pt.type })}
                      className={`p-4 border-2 rounded-lg text-left transition-all ${
                        isSelected
                          ? 'border-indigo-600 bg-indigo-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="flex items-center mb-2">
                        <Icon className={`h-5 w-5 mr-2 ${isSelected ? 'text-indigo-600' : 'text-gray-500'}`} />
                        <span className={`font-medium ${isSelected ? 'text-indigo-600' : 'text-gray-900'}`}>
                          {pt.label}
                        </span>
                      </div>
                      <p className="text-sm text-gray-500">{pt.description}</p>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Step 1: Project Details */}
          {step === 1 && (
            <div className="space-y-4">
              <h3 className="text-lg font-medium mb-4">Project Details</h3>

              {/* Organization Selector - only for provider_admin */}
              {userProfile?.role === 'provider_admin' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <span className="flex items-center gap-2">
                      <Building2 className="w-4 h-4" />
                      Organization (Optional)
                    </span>
                  </label>
                  <select
                    value={selectedOrgId}
                    onChange={(e) => setSelectedOrgId(e.target.value)}
                    className="w-full px-3 py-2 border rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                  >
                    <option value="">Canvassed (Provider)</option>
                    {organizations.map(org => (
                      <option key={org.id} value={org.id}>{org.name}</option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    Select the organization this project belongs to.
                  </p>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Project Name
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 border rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="Spring 2024 Enrollment Campaign"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-3 py-2 border rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                  rows={3}
                  placeholder="Door-to-door canvassing for new student enrollment..."
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Start Date
                  </label>
                  <input
                    type="date"
                    value={formData.startDate}
                    onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                    className="w-full px-3 py-2 border rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    End Date (Optional)
                  </label>
                  <input
                    type="date"
                    value={formData.endDate}
                    onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                    className="w-full px-3 py-2 border rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>
              </div>

              {/* School/Location Address */}
              <div className="mt-6 pt-6 border-t">
                <div className="flex items-center gap-2 mb-4">
                  <MapPin className="w-5 h-5 text-indigo-600" />
                  <h4 className="font-medium text-gray-900">School/Location Address</h4>
                  <span className="text-sm text-gray-500">(Optional)</span>
                </div>
                <p className="text-sm text-gray-500 mb-4">
                  Enter the school or primary location address. This will center the map when creating saturation workloads.
                </p>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Street Address
                    </label>
                    <input
                      type="text"
                      value={formData.schoolAddress}
                      onChange={(e) => setFormData({ ...formData, schoolAddress: e.target.value })}
                      className="w-full px-3 py-2 border rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                      placeholder="123 Main St"
                    />
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        City
                      </label>
                      <input
                        type="text"
                        value={formData.schoolCity}
                        onChange={(e) => setFormData({ ...formData, schoolCity: e.target.value })}
                        className="w-full px-3 py-2 border rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                        placeholder="City"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        State
                      </label>
                      <input
                        type="text"
                        value={formData.schoolState}
                        onChange={(e) => setFormData({ ...formData, schoolState: e.target.value })}
                        className="w-full px-3 py-2 border rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                        placeholder="CA"
                        maxLength={2}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        ZIP Code
                      </label>
                      <input
                        type="text"
                        value={formData.schoolZipCode}
                        onChange={(e) => setFormData({ ...formData, schoolZipCode: e.target.value })}
                        className="w-full px-3 py-2 border rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                        placeholder="12345"
                        maxLength={10}
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      <span className="flex items-center gap-1">
                        <Phone className="w-3.5 h-3.5" />
                        School Phone
                      </span>
                    </label>
                    <input
                      type="tel"
                      value={formData.schoolPhone}
                      onChange={(e) => setFormData({ ...formData, schoolPhone: e.target.value })}
                      className="w-full px-3 py-2 border rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                      placeholder="(555) 123-4567"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Included in appointment confirmation emails to parents.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Goals & Targets (Skip for Survey) */}
          {step === 2 && formData.projectType !== 'survey' && (
            <div className="space-y-4">
              <h3 className="text-lg font-medium mb-4">Goals & Targets</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Valid Lead Goal
                  </label>
                  <input
                    type="number"
                    value={formData.validLeadGoal}
                    onChange={(e) => setFormData({ ...formData, validLeadGoal: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {formData.projectType === 'enrollment' ? 'Enrollment Goal' : 'Enrollment Target'}
                  </label>
                  <input
                    type="number"
                    value={formData.enrollmentGoal}
                    onChange={(e) => setFormData({ ...formData, enrollmentGoal: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>
              </div>
              {formData.projectType === 'enrollment' && (
                <p className="text-sm text-gray-500 mt-2">
                  For enrollment projects, billing and tracking focuses on enrollment goals.
                </p>
              )}

              <div className="mt-6 pt-6 border-t">
                <SchoolSegmentsEditor
                  segments={schoolSegments}
                  onChange={setSchoolSegments}
                  projectType={formData.projectType as ProjectType}
                  defaultConversionRateTarget={25}
                />
              </div>
            </div>
          )}

          {/* Step 2 for Survey: Survey Goals */}
          {step === 2 && formData.projectType === 'survey' && (
            <div className="space-y-4">
              <h3 className="text-lg font-medium mb-4">Survey Goals</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Total Doors to Visit
                  </label>
                  <input
                    type="number"
                    value={formData.totalDoors}
                    onChange={(e) => {
                      const doors = parseInt(e.target.value) || 0;
                      const calculatedGoal = Math.round(doors * (formData.anticipatedSurveyRate / 100));
                      setFormData({
                        ...formData,
                        totalDoors: doors,
                        surveyGoal: calculatedGoal
                      });
                    }}
                    className="w-full px-3 py-2 border rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Anticipated Survey Rate (%)
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={formData.anticipatedSurveyRate}
                    onChange={(e) => {
                      const rate = parseFloat(e.target.value) || 0;
                      const calculatedGoal = Math.round(formData.totalDoors * (rate / 100));
                      setFormData({
                        ...formData,
                        anticipatedSurveyRate: rate,
                        surveyGoal: calculatedGoal
                      });
                    }}
                    className="w-full px-3 py-2 border rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                  />
                  <p className="text-sm text-gray-500 mt-1">Expected completion rate</p>
                </div>
              </div>
              <div className="mt-4 p-4 bg-indigo-50 rounded-lg">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Total Survey Goal
                </label>
                <div className="flex items-center gap-4">
                  <input
                    type="number"
                    value={formData.surveyGoal}
                    onChange={(e) => setFormData({ ...formData, surveyGoal: parseInt(e.target.value) || 0 })}
                    className="w-32 px-3 py-2 border rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                  />
                  <span className="text-sm text-gray-600">
                    = {formData.totalDoors.toLocaleString()} doors × {formData.anticipatedSurveyRate}% rate
                  </span>
                </div>
                <p className="text-sm text-gray-500 mt-1">Auto-calculated, but you can override manually</p>
              </div>
            </div>
          )}

          {/* Step 3: Billing Configuration - provider_admin or primary admin can see/edit */}
          {step === 3 && (
            <div className="space-y-4">
              {canEditBilling ? (
                <>
                  <h3 className="text-lg font-medium mb-4">Billing Configuration</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {formData.projectType === 'enrollment'
                          ? 'Bill Rate per Enrollment ($)'
                          : formData.projectType === 'survey'
                          ? 'Bill Rate per Survey ($)'
                          : 'Bill Rate per Valid Lead ($)'
                        }
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        value={
                          formData.projectType === 'enrollment'
                            ? formData.ratePerEnrollment
                            : formData.projectType === 'survey'
                            ? formData.ratePerSurvey
                            : formData.ratePerValidLead
                        }
                        onChange={(e) => {
                          const value = parseFloat(e.target.value);
                          if (formData.projectType === 'enrollment') {
                            setFormData({ ...formData, ratePerEnrollment: value });
                          } else if (formData.projectType === 'survey') {
                            setFormData({ ...formData, ratePerSurvey: value });
                          } else {
                            setFormData({ ...formData, ratePerValidLead: value });
                          }
                        }}
                        className="w-full px-3 py-2 border rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                      />
                    </div>
                    {isProvider && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Pay Rate ($)
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          value={formData.payRate}
                          onChange={(e) => setFormData({ ...formData, payRate: parseFloat(e.target.value) })}
                          className="w-full px-3 py-2 border rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                        />
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Payment Terms
                    </label>
                    <select
                      value={formData.paymentTerms}
                      onChange={(e) => setFormData({ ...formData, paymentTerms: e.target.value as any })}
                      className="w-full px-3 py-2 border rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                    >
                      <option value="weekly">Weekly</option>
                      <option value="biweekly">Bi-weekly</option>
                      <option value="monthly">Monthly</option>
                    </select>
                  </div>
                </>
              ) : (
                <div className="text-center py-8">
                  <h3 className="text-lg font-medium mb-2">Billing Configuration</h3>
                  <p className="text-gray-500">Billing settings will be configured by an administrator.</p>
                  <p className="text-sm text-gray-400 mt-2">Default rates will be applied to this project.</p>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="p-6 border-t flex justify-between">
          {step > 0 && (
            <button
              onClick={() => setStep(step - 1)}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
            >
              Previous
            </button>
          )}
          <div className="ml-auto space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
            >
              Cancel
            </button>
            {step === 0 ? (
              <button
                onClick={() => setStep(1)}
                disabled={!formData.projectType}
                className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50"
              >
                Next
              </button>
            ) : step < getTotalSteps() - 1 ? (
              <button
                onClick={() => setStep(step + 1)}
                disabled={step === 1 && (!formData.name || !formData.description)}
                className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50"
              >
                Next
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={creating}
                className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50"
              >
                {creating ? 'Creating...' : 'Create Project'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
